import React from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { Navigation, Map as MapIcon, Satellite } from 'lucide-react';

/**
 * Unified Modern Map Controls - Beautiful compact design with Lucide icons
 * Reusable across all Leaflet maps in the application
 */
export const UnifiedMapControls = ({ userLocation, showLayerSwitcher = true }) => {
    const map = useMap();
    const [activeLayer, setActiveLayer] = React.useState('street');
    const containerRef = React.useRef(null);

    // Disable Leaflet's click propagation on mount
    React.useEffect(() => {
        if (containerRef.current) {
            L.DomEvent.disableClickPropagation(containerRef.current);
            L.DomEvent.disableScrollPropagation(containerRef.current);
        }
    }, []);

    const handleCenterOnLocation = () => {
        if (userLocation) {
            map.flyTo(userLocation, 15, { duration: 1 });
        } else {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        const location = [pos.coords.latitude, pos.coords.longitude];
                        map.flyTo(location, 15, { duration: 1 });
                    },
                    (err) => {
                        console.warn('Failed to get location:', err);
                        alert('לא הצלחנו לקבל את המיקום שלך');
                    }
                );
            }
        }
    };

    const handleLayerChange = (layerId) => {
        setActiveLayer(layerId);

        map.eachLayer((layer) => {
            if (layer instanceof L.TileLayer) {
                map.removeLayer(layer);
            }
        });

        if (layerId === 'street') {
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors',
                maxZoom: 19
            }).addTo(map);
        } else {
            L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: '&copy; Esri',
                maxZoom: 19
            }).addTo(map);
        }
    };

    const ControlButton = ({ onClick, isActive, children, title }) => {
        const [isHovered, setIsHovered] = React.useState(false);

        const handleClick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            onClick();
        };

        return (
            <button
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                title={title}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    background: isActive
                        ? 'linear-gradient(135deg, #E5B55F 0%, #D9866A 100%)'
                        : isHovered
                            ? '#FAF9F6'
                            : 'transparent',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isHovered ? 'scale(1.08)' : 'scale(1)',
                    color: isActive ? 'white' : '#444',
                    pointerEvents: 'auto',
                    position: 'relative',
                    zIndex: 1001
                }}
            >
                {children}
            </button>
        );
    };

    return (
        <div
            className="leaflet-top leaflet-right leaflet-control"
            style={{ marginTop: '10px', marginRight: '10px', zIndex: 1000 }}
        >
            <div
                ref={containerRef}
                className="leaflet-bar"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    background: 'rgba(255, 255, 255, 0.92)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    padding: '6px',
                    borderRadius: '14px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    position: 'relative',
                    zIndex: 1000
                }}
            >
                {userLocation && (
                    <ControlButton
                        onClick={handleCenterOnLocation}
                        isActive={false}
                        title="מרכז על המיקום שלי"
                    >
                        <Navigation size={18} strokeWidth={2.5} />
                    </ControlButton>
                )}

                {userLocation && showLayerSwitcher && (
                    <div style={{
                        width: '100%',
                        height: '1px',
                        background: 'rgba(0, 0, 0, 0.06)',
                        margin: '1px 0'
                    }} />
                )}

                {showLayerSwitcher && (
                    <>
                        <ControlButton
                            onClick={() => handleLayerChange('street')}
                            isActive={activeLayer === 'street'}
                            title="מפת רחובות"
                        >
                            <MapIcon size={17} strokeWidth={2.5} />
                        </ControlButton>

                        <ControlButton
                            onClick={() => handleLayerChange('satellite')}
                            isActive={activeLayer === 'satellite'}
                            title="תצלום לוויין"
                        >
                            <Satellite size={17} strokeWidth={2.5} />
                        </ControlButton>
                    </>
                )}
            </div>
        </div>
    );
};
